document.documentElement.classList.add('js');

/* ---------------------------------------------------------------------------
   Product image gallery — main stage + thumbnails, arrows, keyboard nav
--------------------------------------------------------------------------- */
function initGallery(root) {
  const slides = Array.from(root.querySelectorAll('.pg-slide'));
  const thumbs = Array.from(root.querySelectorAll('.pg-thumb'));
  if (slides.length === 0) return null;

  let current = 0;

  function show(index) {
    if (index < 0) index = slides.length - 1;
    if (index >= slides.length) index = 0;
    current = index;
    slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
    thumbs.forEach((t, i) => {
      const active = i === index;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-current', active ? 'true' : 'false');
    });
    // Pause any videos on slides we're leaving
    slides.forEach((s, i) => {
      if (i === index) return;
      s.querySelectorAll('video').forEach((v) => v.pause && v.pause());
    });
    const activeThumb = thumbs[index];
    if (activeThumb) activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  thumbs.forEach((thumb, i) => {
    thumb.addEventListener('click', () => show(i));
  });

  const prev = root.querySelector('[data-gallery-prev]');
  const next = root.querySelector('[data-gallery-next]');
  if (prev) prev.addEventListener('click', () => show(current - 1));
  if (next) next.addEventListener('click', () => show(current + 1));

  root.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') { show(current - 1); }
    else if (e.key === 'ArrowRight') { show(current + 1); }
  });

  // Show a specific media id (used when a variant is selected)
  function showMediaId(mediaId) {
    if (!mediaId) return;
    const idx = slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
    if (idx >= 0) show(idx);
  }

  return { showMediaId };
}

/* ---------------------------------------------------------------------------
   Variant picker — matches selected options to a variant, syncs the form,
   price, add-to-cart button, and gallery image.
--------------------------------------------------------------------------- */
function initVariantPicker(form, gallery) {
  const dataEl = form.parentElement.querySelector('[data-variant-json]');
  if (!dataEl) return;

  let variants = [];
  try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

  const idField = form.querySelector('[data-variant-id]');
  const priceEl = form.parentElement.querySelector('[data-price]');
  const addButton = form.querySelector('[data-add-button]');
  const addLabel = form.querySelector('[data-add-label]');
  const groups = Array.from(form.querySelectorAll('.variant-group'));

  function selectedOptions() {
    return groups.map((group) => {
      const checked = group.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
  }

  function findVariant(opts) {
    return variants.find((v) =>
      opts.every((val, i) => val === null || v.options[i] === val)
    );
  }

  function update() {
    const opts = selectedOptions();

    // reflect chosen values in the labels
    groups.forEach((group) => {
      const idx = Number(group.dataset.optionIndex);
      const label = group.querySelector('[data-option-value]');
      if (label && opts[idx] != null) label.textContent = opts[idx];
    });

    const variant = findVariant(opts) || variants[0];
    if (!variant) return;

    if (idField) idField.value = variant.id;
    if (priceEl) priceEl.textContent = variant.price;

    if (addButton && addLabel) {
      if (variant.available) {
        addButton.removeAttribute('disabled');
        addLabel.textContent = 'Add to Cart';
      } else {
        addButton.setAttribute('disabled', 'disabled');
        addLabel.textContent = 'Sold Out';
      }
    }

    if (gallery && variant.media_id) gallery.showMediaId(variant.media_id);
  }

  form.querySelectorAll('.variant-group input[type="radio"]').forEach((input) => {
    input.addEventListener('change', update);
  });

  // keep pills in sync if the fallback <select> is ever used
  if (idField) {
    idField.addEventListener('change', () => {
      const variant = variants.find((v) => String(v.id) === String(idField.value));
      if (!variant) return;
      groups.forEach((group) => {
        const idx = Number(group.dataset.optionIndex);
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = input.value === variant.options[idx];
        });
      });
      update();
    });
  }

  update();
}

/* ---------------------------------------------------------------------------
   Related products — load genuine per-product recommendations from Shopify
--------------------------------------------------------------------------- */
function initRelatedProducts(section) {
  const url = section.dataset.url;
  const target = section.querySelector('[data-related-grid]');
  if (!url || !target) return;

  fetch(url)
    .then((r) => (r.ok ? r.text() : Promise.reject()))
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-related-grid]');
      if (fresh && fresh.children.length > 0) {
        target.innerHTML = fresh.innerHTML;
      } else {
        // no recommendations available — hide the section rather than show filler
        section.hidden = true;
      }
    })
    .catch(() => { /* keep placeholder on failure */ });
}

/* ---------------------------------------------------------------------------
   Mobile burger menu
--------------------------------------------------------------------------- */
function initNavToggle() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const toggle = header.querySelector('[data-nav-toggle]');
  const nav = header.querySelector('#SiteNav');
  if (!toggle || !nav) return;

  function setOpen(open) {
    header.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
  }

  toggle.addEventListener('click', () => setOpen(!header.classList.contains('is-open')));
  // close after choosing a link
  nav.addEventListener('click', (e) => { if (e.target.closest('a')) setOpen(false); });
  // close on Escape
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setOpen(false); });
}

/* ---------------------------------------------------------------------------
   Cart — quantity steppers with debounced auto-update
--------------------------------------------------------------------------- */
function initCart() {
  const form = document.querySelector('[data-cart-form]');
  if (!form) return;
  const updateBtn = form.querySelector('[name="update"]');

  let timer;
  function scheduleUpdate() {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (form.requestSubmit && updateBtn) form.requestSubmit(updateBtn);
      else form.submit();
    }, 600);
  }

  form.querySelectorAll('.qty-stepper').forEach((stepper) => {
    const input = stepper.querySelector('[data-qty-input]');
    if (!input) return;
    const minus = stepper.querySelector('[data-qty-minus]');
    const plus = stepper.querySelector('[data-qty-plus]');
    if (minus) minus.addEventListener('click', () => {
      input.value = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
      scheduleUpdate();
    });
    if (plus) plus.addEventListener('click', () => {
      input.value = (parseInt(input.value, 10) || 0) + 1;
      scheduleUpdate();
    });
    input.addEventListener('change', scheduleUpdate);
  });
}

/* ---------------------------------------------------------------------------
   Add to cart via AJAX — keeps the customer off the raw /cart/add error page.
   On success we go to the cart; on failure we show a friendly inline message.
--------------------------------------------------------------------------- */
function initProductForm() {
  const form = document.querySelector('.product-form');
  if (!form) return;
  const button = form.querySelector('[data-add-button]');
  const label = form.querySelector('[data-add-label]');
  const errorEl = form.querySelector('[data-cart-error]');

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (errorEl) errorEl.textContent = '';
    const original = label ? label.textContent : '';
    if (button) button.setAttribute('disabled', 'disabled');
    if (label) label.textContent = 'Adding…';

    function reset() {
      if (button) button.removeAttribute('disabled');
      if (label) label.textContent = original || 'Add to Cart';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    })
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (ok) {
          window.location.href = '/cart';
        } else {
          if (errorEl) errorEl.textContent = data.description || data.message || 'Sorry, this item could not be added.';
          reset();
        }
      })
      .catch(() => {
        if (errorEl) errorEl.textContent = 'Something went wrong. Please try again.';
        reset();
      });
  });
}

/* ---------------------------------------------------------------------------
   Collection filters — auto-apply on change (Apply button is the no-JS fallback)
--------------------------------------------------------------------------- */
function initFilters() {
  const form = document.querySelector('[data-filters-form]');
  if (!form) return;
  const submit = () => { if (form.requestSubmit) form.requestSubmit(); else form.submit(); };
  form.querySelectorAll('input[type="checkbox"]').forEach((el) => {
    el.addEventListener('change', submit);
  });
  form.querySelectorAll('.filter-price input').forEach((el) => {
    el.addEventListener('change', submit);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initNavToggle();
  initCart();
  initProductForm();
  initFilters();

  document.querySelectorAll('[data-related-products]').forEach(initRelatedProducts);

  document.querySelectorAll('[data-gallery]').forEach((root) => {
    const gallery = initGallery(root);
    const form = document.querySelector('.product-form');
    if (form) initVariantPicker(form, gallery);
  });

  // product page with a form but no gallery (single/no media)
  if (!document.querySelector('[data-gallery]')) {
    const form = document.querySelector('.product-form');
    if (form) initVariantPicker(form, null);
  }
});
